/**
 * ThemedInput Component
 * 
 * Input field that adapts its appearance based on the active immersive theme.
 * Supports all 5 themes with unique visual characteristics.
 */

import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { useImmersiveTheme } from '../../hooks/useImmersiveTheme';
import { ImmersiveThemeType, ThemedInputProps } from '../../types/immersive-theme.types';
import {
  neumorphismInput,
  neumorphismInputFocus,
  neumorphismInputError,
  neumorphismInputDisabled,
} from './theme-styles/neumorphism';
import {
  brutalistInput,
  brutalistInputFocus,
  brutalistInputError,
  brutalistInputDisabled,
} from './theme-styles/brutalist';
import {
  retroTerminalInput,
  retroTerminalInputFocus,
  retroTerminalInputError,
  retroTerminalInputDisabled,
} from './theme-styles/retro-terminal';
import {
  organicNatureInput,
  organicNatureInputFocus,
  organicNatureInputError,
  organicNatureInputDisabled,
} from './theme-styles/organic-nature';
import {
  luxuryGoldInput,
  luxuryGoldInputFocus,
  luxuryGoldInputError,
  luxuryGoldInputDisabled,
  luxuryGoldInputLabel,
} from './theme-styles/luxury-gold';

// Style maps for each theme
const inputStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: {},
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismInput,
  [ImmersiveThemeType.BRUTALIST]: brutalistInput,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalInput,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureInput,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldInput,
};

const focusStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { outline: '2px solid #3b82f6' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismInputFocus,
  [ImmersiveThemeType.BRUTALIST]: brutalistInputFocus,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalInputFocus,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureInputFocus,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldInputFocus,
};

const errorStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { borderColor: '#ef4444' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismInputError,
  [ImmersiveThemeType.BRUTALIST]: brutalistInputError,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalInputError,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureInputError,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldInputError,
};

const disabledStyles: Record<ImmersiveThemeType, React.CSSProperties> = {
  [ImmersiveThemeType.NONE]: { opacity: 0.5, cursor: 'not-allowed' },
  [ImmersiveThemeType.NEUMORPHISM]: neumorphismInputDisabled,
  [ImmersiveThemeType.BRUTALIST]: brutalistInputDisabled,
  [ImmersiveThemeType.RETRO_TERMINAL]: retroTerminalInputDisabled,
  [ImmersiveThemeType.ORGANIC_NATURE]: organicNatureInputDisabled,
  [ImmersiveThemeType.LUXURY_GOLD]: luxuryGoldInputDisabled,
};

// Size variants
const sizeStyles: Record<string, React.CSSProperties> = {
  sm: { padding: '8px 12px', fontSize: '13px' },
  md: { padding: '12px 16px', fontSize: '14px' },
  lg: { padding: '16px 20px', fontSize: '16px' },
};

export const ThemedInput = forwardRef<HTMLInputElement, ThemedInputProps>(
  (
    {
      type = 'text',
      placeholder,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      disabled = false,
      readOnly = false,
      error,
      label,
      helperText,
      icon,
      iconPosition = 'left',
      size = 'md',
      fullWidth = false,
      className,
      name,
      id,
      autoComplete,
      autoFocus,
      required,
      maxLength,
      minLength,
      pattern,
      ...rest
    },
    ref
  ) => {
    const { theme } = useImmersiveTheme();
    const [isFocused, setIsFocused] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);

    // Blinking cursor effect for RETRO_TERMINAL
    useEffect(() => {
      if (theme === ImmersiveThemeType.RETRO_TERMINAL && isFocused) {
        const interval = setInterval(() => {
          setShowCursor((prev) => !prev);
        }, 500);
        return () => clearInterval(interval);
      }
    }, [theme, isFocused]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Compute styles
    const computedStyles: React.CSSProperties = {
      ...inputStyles[theme],
      ...sizeStyles[size],
      ...(isFocused && !error ? focusStyles[theme] : {}),
      ...(error ? errorStyles[theme] : {}),
      ...(disabled ? disabledStyles[theme] : {}),
      ...(fullWidth ? { width: '100%' } : {}),
      ...(icon ? { paddingLeft: iconPosition === 'left' ? '44px' : undefined, paddingRight: iconPosition === 'right' ? '44px' : undefined } : {}),
    };

    // Container styles
    const containerStyles: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      width: fullWidth ? '100%' : 'auto',
    };

    // Label styles based on theme
    const labelStyles: React.CSSProperties = {
      fontSize: '14px',
      fontWeight: 500,
      color: theme === ImmersiveThemeType.LUXURY_GOLD ? 'var(--lux-text-gold)' : 'inherit',
      ...(theme === ImmersiveThemeType.LUXURY_GOLD ? luxuryGoldInputLabel : {}),
      ...(theme === ImmersiveThemeType.BRUTALIST ? { textTransform: 'uppercase', fontWeight: 900, letterSpacing: '-0.02em' } : {}),
      ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { fontFamily: 'var(--term-font)', textShadow: 'var(--term-text-shadow)' } : {}),
    };

    // Helper text styles
    const helperStyles: React.CSSProperties = {
      fontSize: '12px',
      color: error ? (theme === ImmersiveThemeType.RETRO_TERMINAL ? '#ff4444' : 'var(--color-danger, #ef4444)') : 'var(--color-text-secondary, #6b7280)',
      ...(theme === ImmersiveThemeType.RETRO_TERMINAL ? { fontFamily: 'var(--term-font)', textShadow: error ? '0 0 10px #ff4444' : 'var(--term-text-shadow)' } : {}),
    };

    // Icon wrapper styles
    const iconWrapperStyles: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      ...(iconPosition === 'left' ? { left: '14px' } : { right: '14px' }),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      pointerEvents: 'none',
      color: theme === ImmersiveThemeType.RETRO_TERMINAL ? 'var(--term-text)' : 'inherit',
    };

    // Terminal cursor styles
    const cursorStyles: React.CSSProperties = {
      position: 'absolute',
      right: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '8px',
      height: '16px',
      background: 'var(--term-text)',
      opacity: showCursor ? 1 : 0,
      transition: 'opacity 0.05s',
    };

    return (
      <div style={containerStyles} className={className}>
        {label && (
          <label htmlFor={id || name} style={labelStyles}>
            {label}
            {required && <span style={{ color: 'var(--color-danger, #ef4444)', marginLeft: '4px' }}>*</span>}
          </label>
        )}
        
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {icon && iconPosition === 'left' && (
            <span style={iconWrapperStyles}>{icon}</span>
          )}
          
          <input
            ref={ref || inputRef}
            type={type}
            name={name}
            id={id || name}
            placeholder={theme === ImmersiveThemeType.RETRO_TERMINAL ? `> ${placeholder || ''}` : placeholder}
            value={value}
            defaultValue={defaultValue}
            onChange={onChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            required={required}
            maxLength={maxLength}
            minLength={minLength}
            pattern={pattern}
            style={computedStyles}
            aria-invalid={!!error}
            aria-describedby={helperText || error ? `${id || name}-helper` : undefined}
            {...rest}
          />
          
          {icon && iconPosition === 'right' && (
            <span style={iconWrapperStyles}>{icon}</span>
          )}
          
          {/* Blinking cursor for RETRO_TERMINAL */}
          {theme === ImmersiveThemeType.RETRO_TERMINAL && isFocused && !icon && (
            <span style={cursorStyles} aria-hidden="true" />
          )}
        </div>
        
        {(helperText || error) && (
          <span id={`${id || name}-helper`} style={helperStyles}>
            {error || helperText}
          </span>
        )}
      </div>
    );
  }
);

ThemedInput.displayName = 'ThemedInput';

export default ThemedInput;
